<template>
	<label
		:key="user.id"
		:for="getInputLabel(user.id)"
		class="user-assign-select-user-item"
		:class="classAddons"
	>
		<button
			v-tooltip="user.name"
			class="user-assign-select-user-item__avatar"
			type="button"
			:aria-label="$t('remove_user', { name: user.name })"
			@click="$emit('unselect', user.id)"
		>
			<UserAvatar
				:user
				show-remove
				:availability="availability"
				:extra-used-availability="extraUsedAvailability"
			/>
		</button>

		<SuiTimeInput
			v-if="hasIndividualTimeAllocation && hasCustomAllocation"
			:id="getInputLabel(user.id)"
			:model-value="allocatedTime"
			class="user-assign-select-user-item__time-input"
			size="xsmall"
			variant="unstyled"
			align="center"
			hide-clear-button
			show-zero-values
			@update:model-value="emit('update:allocated-time', $event)"
			@click="$event.target.select()"
			@focus="emit('focus')"
			@blur="emit('blur')"
			@keyup.enter="$event.target.blur()"
		/>
	</label>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { SuiTimeInput } from '@developers/scoro-ui';
import {
	type UserAssignSelectUserFragment,
	type UserAssignSelectAvailabilityQuery,
} from '@/generated/graphql';
import { type TaskAssigneeAllocatedDuration } from '@/features/Task/components/TaskUsers/types';
import UserAvatar from '../../UserAvatar/UserAvatar.vue';
import type { UserAssignSelectRole } from '../../../types';

export interface UserAssignSelectHeaderUserItemProps {
	user: UserAssignSelectUserFragment;
	hasIndividualTimeAllocation: boolean;
	hasCustomAllocation: boolean;
	availability?: UserAssignSelectAvailabilityQuery['TaskUsersAvailability'][number];
	extraUsedAvailability: number;
	allocatedTime: string;
	confirmedAllocatedTimes?: TaskAssigneeAllocatedDuration;
	activeRoleFilterId?: UserAssignSelectRole['id'] | null;
}

const props = defineProps<UserAssignSelectHeaderUserItemProps>();

const emit = defineEmits<{
	(e: 'unselect', id: UserAssignSelectUserFragment['id']): void;
	(e: 'update:allocated-time', value: string): void;
	(e: 'focus'): void;
	(e: 'blur'): void;
}>();

const isDimmed = computed(() => props.activeRoleFilterId !== null);

const classAddons = computed(() => ({
	'user-assign-select-user-item--dimmed': isDimmed.value,
}));

const getInputLabel = (userId: UserAssignSelectUserFragment['id']) => {
	return `time-input-${userId}`;
};
</script>

<style lang="scss" scoped>
.user-assign-select-user-item {
	display: flex;
	align-items: center;
	box-shadow: $scoro-box-shadow-small;
	border-radius: 2.5rem;

	&--dimmed {
		opacity: 0.5;
		pointer-events: none;
		filter: #{'grayscale(1)'};
	}

	&__avatar {
		display: flex;
		align-items: center;
		border-radius: 50%;

		&:hover,
		&:focus-visible {
			--user-avatar-show-remove: 1;

			:deep(.sui-avatar__html) {
				color: transparent;
			}
		}
	}

	&__time-input {
		:deep(.sui-input) {
			--sui-input-color-text: #{$neutral-80};
			--sui-input-bg-color: transparent;

			width: 8.125rem;
			border-radius: 2.5rem;
		}

		:deep(.sui-input__field) {
			&:hover {
				background-color: $neutral-20;
				border-radius: 2.5rem;
				margin: 0.25rem;
			}

			&:hover:not(:focus) {
				cursor: pointer;
			}

			&:focus {
				background-color: $neutral-20;
				border-radius: 2.5rem;
				margin: 0.125rem;
				border: 1px solid $neutral-30;
			}
		}
	}
}
</style>
